import React from "react";
import { styled } from "@mui/material/styles";
import { Typography, Card, CardContent, CardActionArea } from "@mui/material";
import { FlagIcon } from "react-flag-kit";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TheatersOutlinedIcon from "@mui/icons-material/TheatersOutlined";
import CodeIcon from "@mui/icons-material/Code";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoffee,
  faNewspaper,
  faHashtag,
  faDigitalTachograph,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../../../assets/images/lumen-logo.svg";
import { faSnapchat } from "@fortawesome/free-brands-svg-icons";
import propTypes from "prop-types";
import Devices from "@mui/icons-material/Devices";

const PREFIX = "CardButton";

const classes = {
  facebookIcon: `${PREFIX}-facebookIcon`,
  linkedInIcon: `${PREFIX}-linkedInIcon`,
  twitterIcon: `${PREFIX}-twitterIcon`,
  instagramIcon: `${PREFIX}-instagramIcon`,
  snapChatIcon: `${PREFIX}-snapChatIcon`,
  youTubeIcon: `${PREFIX}-youTubeIcon`,
  generalIcon: `${PREFIX}-generalIcon`,
  logoGrayed: `${PREFIX}-logoGrayed`,
  logoColor: `${PREFIX}-logoColor`,
  snapChatGreyed: `${PREFIX}-snapChatGreyed`,
  greyed: `${PREFIX}-greyed`,
  flagGrayed: `${PREFIX}-flagGrayed`,
  container: `${PREFIX}-container`,
  Content: `${PREFIX}-Content`,
  Card: `${PREFIX}-Card`,
  CardActionArea: `${PREFIX}-CardActionArea`,
};

const Root = styled("span")(() => ({
  [`& .${classes.facebookIcon}`]: {
    color: "#3B5998",
  },

  [`& .${classes.linkedInIcon}`]: {
    color: "#0e76a8",
  },

  [`& .${classes.twitterIcon}`]: {
    color: "#1DA1F2",
  },

  [`& .${classes.instagramIcon}`]: {
    color: "#833AB4",
  },

  [`& .${classes.snapChatIcon}`]: {
    color: "#F7F400",
    fontSize: "2.1875rem !important",
    padding: 2,
  },

  [`& .${classes.youTubeIcon}`]: {
    color: "#FF0000",
  },

  [`& .${classes.generalIcon}`]: {
    color: "#00badb",
  },

  [`& .${classes.logoGrayed}`]: {
    webkitFilter: "grayscale(80%)",
    mozFilter: "grayscale(80%)",
    oFilter: "grayscale(80%)",
    msFilter: "grayscale(80%)",
    filter: "grayscale(80%)",
    opacity: "0.7",
    boxSizing: "border-box",
    height: 35,
    padding: "14px 0px 6px",
  },

  [`& .${classes.logoColor}`]: {
    color: "#00badb",
    boxSizing: "border-box",
    height: 35,
    padding: "14px 0px 6px",
  },

  [`& .${classes.snapChatGreyed}`]: {
    color: "rgba(0, 0, 0, 0.54)",
    fontSize: "2.1875rem !important",
    padding: 2,
  },

  [`& .${classes.greyed}`]: {
    color: "rgba(0, 0, 0, 0.54)",
  },

  [`& .${classes.flagGrayed}`]: {
    webkitFilter: "grayscale(80%)",
    mozFilter: "grayscale(80%)",
    oFilter: "grayscale(80%)",
    msFilter: "grayscale(80%)",
    filter: "grayscale(80%)",
    opacity: "0.7",
  },

  [`&.${classes.container}`]: {
    display: "flex",
  },

  [`& .${classes.Content}`]: {
    display: "grid",
    justifyItems: "center",
    gap: "8px",
    minWidth: "64px",
    padding: "10px 15px",
    verticalAlign: "middle",
    width: "100%",
  },

  [`& .${classes.Card}`]: {
    padding: "0px",
    minWidth: "124px",
    minHeight: "104px",
  },

  [`& .${classes.CardActionArea}`]: {
    height: "100%",
  },
}));

export default function CardButton({ color, iconName, onClick, label }) {
  const selectIcon = () => {
    switch (iconName) {
      case "ourSurvey":
        return (
          <img
            src={logo}
            alt="logo"
            className={color ? classes.logoColor : classes.logoGrayed}
          />
        );
      case "yourSurvey":
        return (
          <AssignmentIndOutlinedIcon
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );
      case "US":
        return (
          <FlagIcon
            code="US"
            size={50}
            className={color ? "" : classes.flagGrayed}
          />
        );
      case "UK":
        return (
          <FlagIcon
            code="GB"
            size={50}
            className={color ? "" : classes.flagGrayed}
          />
        );
      case "japan":
        return (
          <FlagIcon
            code="JP"
            size={50}
            className={color ? "" : classes.flagGrayed}
          />
        );
      case "france":
        return (
          <FlagIcon
            code="FR"
            size={50}
            className={color ? "" : classes.flagGrayed}
          />
        );
      case "fb":
        return (
          <FacebookIcon
            fontSize="large"
            className={color ? classes.facebookIcon : classes.greyed}
          />
        );
      case "tw":
        return (
          <TwitterIcon
            fontSize="large"
            className={color ? classes.twitterIcon : classes.greyed}
          />
        );
      case "yt":
        return (
          <YouTubeIcon
            fontSize="large"
            className={color ? classes.youTubeIcon : classes.greyed}
          />
        );
      case "li":
        return (
          <LinkedInIcon
            fontSize="large"
            className={color ? classes.linkedInIcon : classes.greyed}
          />
        );
      case "ig":
        return (
          <InstagramIcon
            fontSize="large"
            className={color ? classes.instagramIcon : classes.greyed}
          />
        );
      case "sc":
        return (
          <FontAwesomeIcon
            className={color ? classes.snapChatIcon : classes.snapChatGreyed}
            icon={faSnapchat}
          />
        );
      case "digital":
        return (
          <FontAwesomeIcon
            className={color ? classes.generalIcon : classes.greyed}
            icon={faDigitalTachograph}
            size="lg"
          />
        );
      case "social":
        return (
          <FontAwesomeIcon
            className={color ? classes.generalIcon : classes.greyed}
            icon={faHashtag}
            size="lg"
          />
        );
      case "print":
        return (
          <FontAwesomeIcon
            className={color ? classes.generalIcon : classes.greyed}
            icon={faNewspaper}
            size="lg"
          />
        );
      case "Mobile":
        return (
          <SmartphoneRoundedIcon
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );
      case "Webcam":
        return (
          <DesktopWindowsIcon
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );
      case "Lab":
      case "All Devices":
        return (
          <Devices
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );
      case "image":
        return (
          <ImageOutlinedIcon
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );
      case "video":
        return (
          <TheatersOutlinedIcon
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );
      case "skin":
        return (
          <CodeIcon
            fontSize="large"
            className={color ? classes.generalIcon : classes.greyed}
          />
        );

      default:
        return (
          <FontAwesomeIcon
            className={color ? classes.generalIcon : classes.greyed}
            icon={faCoffee}
            size="lg"
          />
        );
    }
  };

  return (
    <Root className={classes.container}>
      <Card
        className={classes.Card}
        aria-labelledby="form-dialog-title"
        justify="center"
      >
        <CardActionArea
          id={iconName}
          centerRipple
          onClick={onClick}
          className={classes.CardActionArea}
        >
          <CardContent className={classes.Content}>
            {selectIcon()}

            <Typography
              gutterBottom
              variant="body2"
              color="textSecondary"
              component={"span"}
            >
              {label === "Lab" ? "All Devices" : label}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Root>
  );
}

CardButton.propTypes = {
  color: propTypes.bool,
  iconName: propTypes.string,
  onClick: propTypes.func,
  label: propTypes.string,
};
